<template>
  <v-card
    data-component-id="RecruiterStatusEmploymentPromise"
  >
    <v-card-title
      class="text-h5"
    >
      Envoyer promesse d'embauche
    </v-card-title>

    <v-progress-linear
      v-if="isLoading"
      indeterminate
      color="primary"
    />

    <v-card-text>
      <v-form
        v-model="isValidForm"
      >
        <template
          v-for="(field, fieldIndex) in fields"
        >
          <component
            :is="field.component ?? 'GenericField'"
            v-if="!field.hidden"
            :id="field.name"
            :key="fieldIndex"
            v-model="data[field.name]"
            :name="field.name"
            :items="getFieldItems(field)"
            :input="field"
            :data="data"
            :prepend-icon="''"
            :field="field"
            :clearable="field.clearable"
            :classes="getInputClasses(field)"
            :rules="field.rules"
            @input="(value) => addLogic(field, value)"
          />
        </template>
      </v-form>
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn
        text
        @click="handleDisagree"
      >
        Annuler
      </v-btn>
      <v-btn
        :disabled="!isValidForm || isLoading"
        color="primary"
        text
        @click="handleAccept"
      >
        Accepter
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex';

import moment from 'moment';

import { requests } from '@/mixins/requests';

import GenericField from '@/components/dynamic/form/templates/GenericField.vue';
import DatePicker from '@/components/dynamic/form/components/DatePicker.vue';

export default {
  name: 'RecruiterStatusEmploymentPromise',
  components: { GenericField, DatePicker },
  props: {
    item: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      fields: [
        {
          name: 'notificationType',
          type: 'text',
          viewType: 'Select',
          label: 'Type de notification',
          optionsByReference: 'notificationType',
          clearable: true,
          mandatory: true,
          required: true
        },
        {
          name: 'seasonYear',
          type: 'text',
          viewType: 'Select',
          label: 'Saison',
          optionsByReference: 'notificationSeasonYear',
          hidden: true,
          mandatory: true,
          required: true,
          showWhen: ['VACATION_EMPLOYMENT_MAIL']
        },
        {
          name: 'companyName',
          type: 'text',
          viewType: 'Text',
          label: 'Nom de l’entreprise ou organisation',
          placeholder: 'Entreprise ou organisation qui émet la promesse d’embauche',
          mandatory: true,
          required: true,
          hidden: true,
          showWhen: ['FIXED_TERM_EMPLOYMENT_PROMISE', 'PERMANENT_EMPLOYMENT_PROMISE']
        },
        {
          name: 'type',
          type: 'text',
          viewType: 'Select',
          label: 'Statut',
          optionsByReference: 'notificationStatus',
          mandatory: true,
          required: true,
          hidden: true,
          showWhen: ['FIXED_TERM_EMPLOYMENT_PROMISE', 'PERMANENT_EMPLOYMENT_PROMISE']
        },
        {
          name: 'startDate',
          component: 'DatePicker',
          type: 'text',
          viewType: 'Date Picker',
          label: 'Date de début de contrat',
          max: 'endDate',
          computed: 'offer.startDate',
          hidden: true,
          mandatory: true,
          required: true,
          showWhen: ['VACATION_EMPLOYMENT_MAIL', 'VACATION_REASSIGNMENT_MAIL', 'FIXED_TERM_EMPLOYMENT_PROMISE', 'PERMANENT_EMPLOYMENT_PROMISE']
        },
        {
          name: 'endDate',
          component: 'DatePicker',
          type: 'text',
          viewType: 'Date Picker',
          label: 'Date de fin de contrat',
          min: 'startDate',
          computed: 'offer.endDate',
          hidden: true,
          mandatory: true,
          required: true,
          showWhen: ['VACATION_EMPLOYMENT_MAIL', 'FIXED_TERM_EMPLOYMENT_PROMISE']
        },
        {
          name: 'numberWeeks',
          type: 'Float',
          viewType: 'Number',
          label: 'Nombre de semaines',
          placeholder: 'Délai maximal en semaines pour un retour du candidat',
          hidden: true,
          mandatory: true,
          required: true,
          rules: [
            value => {
              const positiveIntegerRegex = /^\d+$/;
              return positiveIntegerRegex.test(value) || 'Format de nombre invalide, veuillez saisir un nombre entier positif';
            }
          ],
          showWhen: ['VACATION_EMPLOYMENT_MAIL']
        },
        {
          name: 'numberMonths',
          type: 'Int',
          viewType: 'Number',
          label: 'Nombre de mois',
          placeholder: 'Durée en mois du contrat CDD',
          hidden: true,
          mandatory: true,
          required: true,
          rules: [
            value => {
              const positiveIntegerRegex = /^\d+$/;
              return positiveIntegerRegex.test(value) || 'Format de nombre invalide, veuillez saisir un nombre entier positif';
            }
          ],
          showWhen: ['VACATION_REASSIGNMENT_MAIL']
        },
        {
          name: 'salary',
          type: 'text',
          viewType: 'Text',
          label: 'Rémunération',
          defaultValue: 'Salaire selon profil',
          placeholder: 'Salaire mensuel brut',
          computed: 'offer.salary',
          mandatory: true,
          required: true,
          hidden: true,
          showWhen: ['VACATION_REASSIGNMENT_MAIL', 'FIXED_TERM_EMPLOYMENT_PROMISE', 'PERMANENT_EMPLOYMENT_PROMISE']
        },
        {
          name: 'hoursPerWeek',
          type: 'Int',
          viewType: 'Number',
          label: 'Durée hebdomadaire de travail',
          placeholder: 'Nombre d’heures hebdomadaire de travail',
          mandatory: true,
          required: true,
          hidden: true,
          showWhen: ['FIXED_TERM_EMPLOYMENT_PROMISE', 'PERMANENT_EMPLOYMENT_PROMISE']
        },
        {
          name: 'emailCC',
          type: 'text',
          viewType: 'Text',
          label: 'E-mail Manager (CC)',
          placeholder: 'E-mail Directeur.trice de centre ou futur manager du candidat',
          mandatory: true,
          required: true,
          hidden: true,
          rules: [
            value => {
              const emailRegex = /^(?=[^\s@]{0,64}@)[^\s@]+@[^\s@]+\.[^\s@.]+$/;
              return emailRegex.test(value) || 'Format d\'email invalide';
            }
          ],
          showWhen: ['VACATION_EMPLOYMENT_MAIL', 'VACATION_REASSIGNMENT_MAIL']
        },
        {
          name: 'concent',
          type: 'text',
          viewType: 'Switch',
          label: 'Souhaitez-vous envoyer la promesse d’embauche ?',
          fullWidth: true,
          mandatory: true,
          required: true,
          hidden: true
        }
      ],
      data: {},
      isValidForm: false,
      isLoading: false
    };
  },
  computed: { ...mapState(['user', 'references']) },
  watch: {
    item: {
      handler () {
        this.initializeData();
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    getNestedValue (obj, path) {
      return path
        .split('.')
        .reduce((acc, part) => (acc && acc[part]), obj);
    },
    formatDateTime (value) {
      if (!value) return null;

      // Convert string number to actual number if needed
      if (value.match(/^\d{10}$/)) {
        return new Date(parseInt(value) * 1000)
          .toISOString()
          .replace('.000Z', '+00:00');
      }

      // Already in correct format
      return value;
    },
    getFormatedDate (date) {
      return moment(date).format('DD/MM/YYYY');
    },
    initializeData () {
      const item = JSON.parse(JSON.stringify(this.item));

      this.fields.forEach(field => {
        let value = field.computed
          ? this.getNestedValue(item, field.computed)
          : item[field.name];

        // Format date if needed
        if (field.component === 'DatePicker') {
          value = this.formatDateTime(value);
        }

        this.$set(this.data, field.name, value);
      });
    },
    async saveApplication (input) {
      const response = await requests.saveApplication({
        input,
        isEditMode: true
      });

      if (!response.success) {
        this.$store.commit('setSnackbar', { text: 'Erreur lors de l\'enregistrement.', color: 'error' });
        this.handleDisagree();
      } else {
        this.$store.commit('setSnackbar', { text: 'Enregistrer avec succès.', color: 'success' });
      }
    },
    async handleAccept () {
      this.$emit('handle-loading', true);
      this.isLoading = true;

      const { seasonYear, startDate, endDate, ...restData } = this.data;
      const { collaborator, offer } = this.item;
      const { job } = offer;

      delete restData.concent;

      const promise = {
        ...restData,
        collaboratorUuid: collaborator.uuid,
        status: 'PENDING',
        sendPromiseEmail: true
      };

      if (seasonYear) {
        const seasonYearAux = seasonYear.split('_');
        const season = seasonYearAux[0];
        const year = seasonYearAux[1];

        promise.season = season;
        promise.year = parseInt(year);
      }

      promise.startDate = this.getFormatedDate(startDate);

      if (endDate) {
        promise.endDate = this.getFormatedDate(endDate);
      }

      promise.jobTitle = job.name;
      if (job.convention) {
        promise.convention = job.convention.name;
        promise.group = job.convention.groups[0].name;
      }

      await this.saveApplication({ uuid: this.item.uuid, recruiterStatus: 'SEND_PROMISE_OF_EMPLOYMENT', promise });

      this.isLoading = false;
      this.$emit('handle-loading', false);

      this.$emit('close-dialog');
    },
    handleDisagree () {
      this.$emit('disagree');
      this.$emit('handle-loading', false);
    },
    getFieldItems (field) {
      if (!field.optionsByReference) return [];

      if (field.optionsByReference === 'notificationSeasonYear') {
        const currentYear = moment().year();
        const nextYear = moment().add(1, 'year').year();
        const seasons = Object.entries(this.references.notificationSeasonYear || {});

        return seasons.reduce((acc, [key, value]) => [
          ...acc,
          {
            text: moment(`${currentYear}`).format(`[${value}] YYYY`),
            value: `${key}_${currentYear}`
          },
          {
            text: moment(`${nextYear}`).format(`[${value}] YYYY`),
            value: `${key}_${nextYear}`
          }
        ], []);
      }

      return Object.entries(this.references[field.optionsByReference])
        .map(([key, value]) => ({ text: value, value: key }));
    },
    getInputClasses (field) {
      const classes = [field.mandatory ? 'required' : ''];

      if (this.isViewMode) classes.push('input-color-black');

      return classes.join(' ');
    },
    addLogic (field, value) {
      if (field.name === 'notificationType') {
        const concent = this.fields.find(f => f.name === 'concent');

        this.fields.forEach(f => {
          if (f.showWhen) {
            this.$set(f, 'hidden', !f.showWhen.includes(value));
          }
        });

        if (value) {
          if (concent) {
            this.$set(concent, 'hidden', false);
          }
        } else {
          this.$set(concent, 'hidden', true);
        }
      }
    }
  }
};
</script>
